import { useEffect, useCallback } from 'react';

/**
 * 메가메뉴 이벤트 리스너 관리 훅
 * 위치 관리는 MegaMenu.jsx 컴포넌트에서 직접 처리
 */
export const useMegaMenu = (menuData, mainMenuRef) => {

  // 메가메뉴 이벤트 리스너 설정
  const setupMegaMenuListeners = useCallback(() => {
    const megaMenuDropdown = document.querySelector('.mega-menu-dropdown');
    const mainMenu = (mainMenuRef?.current?.mainMenuRef) || document.querySelector('.header-area .main-menu');

    if (!megaMenuDropdown || !mainMenu) return;

    const hideMegaMenu = () => {
      return new Promise((resolve) => {
        // 메가메뉴가 사라질 때 child-hovered 클래스 제거
        const allMenuItems = document.querySelectorAll('.main-menu-item[data-menu-index]');
        const allSections = document.querySelectorAll('.mega-menu-section[data-menu-index]');
        allMenuItems.forEach(item => item.classList.remove('child-hovered'));
        allSections.forEach(section => section.classList.remove('child-hovered'));

        if (megaMenuDropdown) {
          megaMenuDropdown.style.opacity = '';
          megaMenuDropdown.style.visibility = '';
          megaMenuDropdown.style.transform = '';
          megaMenuDropdown.style.pointerEvents = '';
          megaMenuDropdown.classList.add('hidden');
          requestAnimationFrame(() => resolve());
        } else {
          resolve();
        }
      });
    };

    const isMobileMenuActive = () => {
      const mobileButton = document.querySelector('.mobile-menu-active-button');
      if (!mobileButton) return false;
      const style = window.getComputedStyle(mobileButton);
      const isVisible = style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
      const isMobileWidth = window.matchMedia
        ? window.matchMedia('(max-width: 991px)').matches
        : window.innerWidth <= 991;
      return isVisible && isMobileWidth;
    };

    const enforceHiddenOnMobile = () => {
      if (!megaMenuDropdown || !isMobileMenuActive()) return;
      megaMenuDropdown.style.opacity = '';
      megaMenuDropdown.style.visibility = '';
      megaMenuDropdown.style.transform = '';
      megaMenuDropdown.style.pointerEvents = '';
      megaMenuDropdown.classList.add('hidden');
    };

    // 모바일에서는 항상 숨김 상태를 유지
    enforceHiddenOnMobile();

    // 메뉴 영역 hover
    const handleMenuMouseEnter = () => {
      if (isMobileMenuActive()) return;
      // 메가메뉴 표시 (위치는 MegaMenu 컴포넌트에서 자동 관리)
      if (megaMenuDropdown.classList.contains('hidden')) {
        megaMenuDropdown.classList.remove('hidden');
      }
    };

    const handleMenuMouseLeave = (e) => {
      const relatedTarget = e.relatedTarget;
      if (relatedTarget && (
        megaMenuDropdown.contains(relatedTarget) ||
        megaMenuDropdown === relatedTarget ||
        relatedTarget.closest('.mega-menu-dropdown')
      )) {
        return;
      }
      hideMegaMenu();
    };

    // 메뉴 항목 클릭 이벤트 (터치 디바이스 대응)
    const handleMenuClick = (e) => {
      if (isMobileMenuActive()) return;
      const menuItem = e.target.closest('.main-menu-item[data-menu-index]');
      if (menuItem) {
        // 메가메뉴 표시
        if (megaMenuDropdown.classList.contains('hidden')) {
          megaMenuDropdown.classList.remove('hidden');
          // 위치 업데이트
          if (typeof window.updateMegaMenuTop === 'function') {
            window.updateMegaMenuTop();
          }
        }
      }
    };

    mainMenu.addEventListener('mouseenter', handleMenuMouseEnter);
    mainMenu.addEventListener('mouseleave', handleMenuMouseLeave);
    mainMenu.addEventListener('touchstart', enforceHiddenOnMobile, { passive: true });

    // 클릭 이벤트: mainMenu 전체에 위임 (더보기 드롭다운 내 항목 포함)
    mainMenu.addEventListener('click', handleMenuClick);

    const handleResizeOrRotate = () => {
      enforceHiddenOnMobile();
    };
    window.addEventListener('resize', handleResizeOrRotate, { passive: true });
    window.addEventListener('orientationchange', handleResizeOrRotate, { passive: true });

    // 메가메뉴 hover (위치는 MegaMenu 컴포넌트에서 자동 관리)

    // 메뉴 항목 hover 시 하이라이트 (mainMenu 전체 위임 - 더보기 드롭다운 항목 포함)
    const handleMenuOver = (e) => {
      const menuItem = e.target.closest('.main-menu-item[data-menu-index]');
      if (menuItem) {
        const menuIndex = menuItem.getAttribute('data-menu-index');
        const allSections = document.querySelectorAll('.mega-menu-section[data-menu-index]');
        allSections.forEach(section => section.classList.remove('highlighted'));
        const targetSection = document.querySelector(`.mega-menu-section[data-menu-index="${menuIndex}"]`);
        if (targetSection) {
          targetSection.classList.add('highlighted');
        }
      }
    };

    const handleMenuOut = (e) => {
      const menuItem = e.target.closest('.main-menu-item[data-menu-index]');
      if (menuItem) {
        const relatedTarget = e.relatedTarget;
        if (relatedTarget && (
          megaMenuDropdown && (
            megaMenuDropdown.contains(relatedTarget) ||
            relatedTarget.closest('.mega-menu-dropdown')
          )
        )) {
          return;
        }
        const allSections = document.querySelectorAll('.mega-menu-section[data-menu-index]');
        allSections.forEach(section => section.classList.remove('highlighted'));
      }
    };

    mainMenu.addEventListener('mouseover', handleMenuOver);
    mainMenu.addEventListener('mouseout', handleMenuOut);

    // 메가메뉴 영역에서 마우스가 나갈 때
    const handleMegaMenuLeave = (e) => {
      const relatedTarget = e.relatedTarget;
      // 메뉴로 돌아가는 경우만 유지, .logo 등 다른 영역으로 가면 메가메뉴 닫기
      if (relatedTarget && (
        mainMenu && (
          mainMenu.contains(relatedTarget) ||
          relatedTarget.closest('.main-menu')
        )
      )) {
        return;
      }
      // 메가메뉴에서 나가서 .main-menu 밖(로고 등)으로 갔을 때 메가메뉴 숨김
      hideMegaMenu();
      // 메가메뉴 영역에서 나갈 때 모든 하이라이트 및 child-hovered 제거
      const allSections = document.querySelectorAll('.mega-menu-section[data-menu-index]');
      const allMenuItems = document.querySelectorAll('.main-menu-item[data-menu-index]');
      allSections.forEach(section => {
        section.classList.remove('highlighted');
        section.classList.remove('child-hovered');
      });
      allMenuItems.forEach(item => item.classList.remove('child-hovered'));
    };

    megaMenuDropdown.addEventListener('mouseleave', handleMegaMenuLeave);

    // 메가메뉴 하위 항목 hover 시 부모 항목 하이라이트
    let handleMegaMenuLinkOver = null;
    let handleMegaMenuLinkOut = null;

    handleMegaMenuLinkOver = (e) => {
      const megaMenuLink = e.target.closest('.mega-menu-link');
      if (megaMenuLink) {
        // 부모 섹션 찾기
        const parentSection = megaMenuLink.closest('.mega-menu-section[data-menu-index]');
        if (parentSection) {
          const menuIndex = parentSection.getAttribute('data-menu-index');

          // 모든 하이라이트 제거
          const allMenuItems = document.querySelectorAll('.main-menu-item[data-menu-index]');
          const allSections = document.querySelectorAll('.mega-menu-section[data-menu-index]');
          allMenuItems.forEach(item => item.classList.remove('child-hovered'));
          allSections.forEach(section => section.classList.remove('child-hovered'));

          // 해당 부모 항목에 하이라이트 추가
          const targetMenuItem = document.querySelector(`.main-menu-item[data-menu-index="${menuIndex}"]`);
          if (targetMenuItem) {
            targetMenuItem.classList.add('child-hovered');
          }
          if (parentSection) {
            parentSection.classList.add('child-hovered');
          }
        }
      }
    };

    handleMegaMenuLinkOut = (e) => {
      const megaMenuLink = e.target.closest('.mega-menu-link');
      if (megaMenuLink) {
        const relatedTarget = e.relatedTarget;
        // 메가메뉴 영역 내에서 이동하는 경우는 유지
        if (relatedTarget && (
          megaMenuDropdown && (
            megaMenuDropdown.contains(relatedTarget) ||
            relatedTarget.closest('.mega-menu-dropdown')
          )
        )) {
          return;
        }

        // 모든 하이라이트 제거
        const allMenuItems = document.querySelectorAll('.main-menu-item[data-menu-index]');
        const allSections = document.querySelectorAll('.mega-menu-section[data-menu-index]');
        allMenuItems.forEach(item => item.classList.remove('child-hovered'));
        allSections.forEach(section => section.classList.remove('child-hovered'));
      }
    };

    // 메가메뉴 드롭다운에 이벤트 위임
    megaMenuDropdown.addEventListener('mouseover', handleMegaMenuLinkOver);
    megaMenuDropdown.addEventListener('mouseout', handleMegaMenuLinkOut);

    // 메가메뉴 링크 클릭 - React 컴포넌트에서 처리하므로 여기서는 비활성화
    // React 컴포넌트의 onClick 핸들러가 먼저 실행되도록 함
    // 원본 JavaScript는 React Router와 충돌하므로 제거
    // const handleMegaMenuClick = async (e) => {
    //   // React 컴포넌트에서 처리하므로 여기서는 처리하지 않음
    // };
    // megaMenuDropdown.addEventListener('click', handleMegaMenuClick);

    // 메가메뉴 외부 클릭
    const handleDocumentClick = (e) => {
      if (megaMenuDropdown &&
          !megaMenuDropdown.contains(e.target) &&
          mainMenu &&
          !mainMenu.contains(e.target)) {
        hideMegaMenu();
      }
    };

    document.addEventListener('click', handleDocumentClick);

    return () => {
      mainMenu.removeEventListener('mouseenter', handleMenuMouseEnter);
      mainMenu.removeEventListener('mouseleave', handleMenuMouseLeave);
      mainMenu.removeEventListener('touchstart', enforceHiddenOnMobile);
      megaMenuDropdown.removeEventListener('mouseleave', handleMegaMenuLeave);
      if (handleMegaMenuLinkOver && handleMegaMenuLinkOut) {
        megaMenuDropdown.removeEventListener('mouseover', handleMegaMenuLinkOver);
        megaMenuDropdown.removeEventListener('mouseout', handleMegaMenuLinkOut);
      }
      document.removeEventListener('click', handleDocumentClick);
      window.removeEventListener('resize', handleResizeOrRotate);
      window.removeEventListener('orientationchange', handleResizeOrRotate);
      mainMenu.removeEventListener('mouseover', handleMenuOver);
      mainMenu.removeEventListener('mouseout', handleMenuOut);
      mainMenu.removeEventListener('click', handleMenuClick);
    };
  }, [mainMenuRef]);

  // 초기화
  useEffect(() => {
    // requestAnimationFrame을 Promise로 감싸서 async/await 사용 가능하게
    const waitForFrame = () => new Promise(resolve => requestAnimationFrame(resolve));

    // 메뉴가 실제로 렌더링되었는지 확인
    const waitForMenuRendered = async () => {
      return new Promise((resolve) => {
        // menuData가 있고 실제로 메뉴가 DOM에 렌더링되었는지 확인
        const checkMenuRendered = () => {
          const mainMenuNav = document.querySelector('.main-menu > nav');
          const mainMenuList = document.querySelector('.main-menu-list');

          // 메뉴가 렌더링되었는지 확인 (menuData가 있고 실제 DOM 요소가 존재)
          if (menuData && menuData.length > 0) {
            if (mainMenuNav && mainMenuList && mainMenuList.children.length > 0) {
              return true;
            }
          } else {
            // menuData가 없어도 메뉴가 렌더링될 수 있음 (기본 메뉴 등)
            if (mainMenuNav && mainMenuList) {
              return true;
            }
          }
          return false;
        };

        if (checkMenuRendered()) {
          // 이미 렌더링됨 - 몇 프레임 대기 후 resolve
          waitForFrame().then(() => {
            waitForFrame().then(() => resolve());
          });
          return;
        }

        // MutationObserver로 메뉴 렌더링 대기
        const observer = new MutationObserver((mutations, obs) => {
          if (checkMenuRendered()) {
            obs.disconnect();
            waitForFrame().then(() => {
              waitForFrame().then(() => resolve());
            });
          }
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true
        });

        // ResizeObserver로도 확인 (메뉴가 렌더링되면 크기가 변경됨)
        const mainMenuNav = document.querySelector('.main-menu > nav');
        if (mainMenuNav && window.ResizeObserver) {
          const resizeObserver = new ResizeObserver(() => {
            if (checkMenuRendered()) {
              resizeObserver.disconnect();
              observer.disconnect();
              waitForFrame().then(() => {
                waitForFrame().then(() => resolve());
        });
            }
          });
          resizeObserver.observe(mainMenuNav);
        }
      });
    };

    // window.load 이벤트 대기 함수
    const waitForLoad = async () => {
      if (document.readyState !== 'complete') {
        await new Promise(resolve => {
          window.addEventListener('load', resolve, { once: true });
        });
      }
      // load 후에도 몇 프레임 대기
      await waitForFrame();
      await waitForFrame();
    };

    // 초기화 함수
    const initialize = async () => {
      // window.load 이벤트 대기 (페이지 로딩 완료)
      await waitForLoad();

      // 메뉴가 실제로 렌더링될 때까지 대기 (DB에서 메뉴 데이터 로드 후)
      await waitForMenuRendered();

      // 메가메뉴 이벤트 리스너 설정
      setupMegaMenuListeners();
    };

    // 초기화 실행
    initialize();
  }, [menuData, mainMenuRef, setupMegaMenuListeners]);
};

